import { useLazyQuery } from "@apollo/client/react";
import type { ReactNode } from "react";
import { PaperclipIcon } from "lucide-react";

import { Icon } from "@/components/atoms/Icon";
import { Popper } from "@/components/atoms/Popper";
import { PdfPreview } from "@/components/molecules/PdfPreview";
import { Queries } from "@/constants";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

/** The two kinds of file a glance can show; anything else is only named. */
const IS_IMAGE = /\.(avif|bmp|gif|jpe?g|png|svg|webp)$/i;
const IS_PDF = /\.pdf$/i;

/**
 * A file the user sent, as they sent it: named on the message it went with and
 * on the call reading it, turning while that runs, and showing itself when
 * someone looks at it.
 */
export function FileChip({
  id,
  name,
  isWorking = false,
  status,
}: {
  /** The file's global id, for reading what it is when someone looks. */
  id?: string | null;
  name: string;
  isWorking?: boolean;
  /** How it went, shown once whatever was working on it has finished. */
  status?: ReactNode;
}) {
  // Read only when someone looks at it: a chip nobody hovers costs nothing,
  // and a storage URL is only good for a while after it is made.
  const [load, { data, loading }] = useLazyQuery(Queries.MESSAGE_FILE_QUERY, {
    fetchPolicy: "cache-first",
  });
  const file =
    data?.node?.__typename === "MessageFileObject" ? data.node.file : null;
  const url = file?.url ?? null;
  const shownAs = file?.name ?? name;

  const chip = (
    <div
      data-testid="file-chip"
      data-file-name={name}
      onMouseEnter={() => id && load({ variables: { id } })}
      className="flex items-center gap-1.5 pt-0.5 pb-0.5"
    >
      <Badge
        variant="secondary"
        // A file worth looking at says so under the pointer. One with nothing
        // behind it yet — still uploading — is just its name.
        className={cn(id && "cursor-pointer")}
      >
        {isWorking ? (
          <Spinner aria-label={`Reading ${name}`} />
        ) : (
          <Icon ReactIcon={PaperclipIcon} size="sm" />
        )}
        {name}
      </Badge>
      {status}
    </div>
  );

  // Nothing to look up, nothing to look at: the chip is the whole of it.
  if (!id) return chip;

  return (
    <Popper
      isHoverable
      isClickable={false}
      autoFocus={false}
      placement="right-start"
      anchor={chip}
    >
      <div className="w-105 h-130 flex items-center justify-center overflow-hidden rounded-md bg-muted">
        {loading ? (
          // The shape of what is coming, as everything else waits.
          <Skeleton
            variant="rectangular"
            className="w-[88%] h-[92%] rounded-sm"
          />
        ) : !url ? (
          <Typography level="body-sm" className="text-muted-foreground/80">
            {name}
          </Typography>
        ) : IS_IMAGE.test(shownAs) ? (
          <img
            src={url}
            alt={name}
            className="max-w-full max-h-full object-contain"
          />
        ) : IS_PDF.test(shownAs) ? (
          // Drawn by the app, as the document panel draws the same file: its
          // pages as pictures, with none of what else a PDF can carry. The
          // browser's own viewer would not do — it is a frame we cannot see
          // into, and it draws nothing at all in a sandboxed one.
          <div className="w-full h-full overflow-y-auto">
            <PdfPreview url={url} />
          </div>
        ) : (
          <Typography level="body-sm" className="text-muted-foreground/80">
            {name}
          </Typography>
        )}
      </div>
    </Popper>
  );
}
